import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2 } from "lucide-react";

import { useAuth } from "@/auth/auth-provider";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchableSelector } from "@/components/searchable-selector";
import esumeiLogoUrl from "@/assets/brand/esumei-logo.svg";
import anietronicLogoUrl from "@/assets/brand/anietronic-logo-grey.jpeg";

const loginSchema = z.object({
  email: z.string().min(3, "Email is required").email("Enter a valid email"),
  password: z.string().min(1, "Password is required"),
});
type LoginValues = z.infer<typeof loginSchema>;

export function LoginPage(): JSX.Element {
  const {
    loading,
    previewMode,
    previewUsers,
    selectedPreviewSubject,
    me,
    sessionExpired,
    setPreviewSubject,
    loginWithPassword,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });

  useEffect(() => {
    if (me) {
      const from = (
        location.state as { from?: { pathname?: string; search?: string; hash?: string } } | null
      )?.from;
      navigate(
        from
          ? { pathname: from.pathname ?? "/dashboard", search: from.search, hash: from.hash }
          : "/dashboard",
        { replace: true },
      );
    }
  }, [me, navigate, location.state]);

  async function onSubmit(values: LoginValues): Promise<void> {
    setSubmitError(null);
    try {
      await loginWithPassword(values.email, values.password);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Sign-in failed");
    }
  }

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center text-muted-foreground">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading…
      </div>
    );
  }

  return (
    <div className="grid min-h-screen w-full lg:grid-cols-2">
      <div className="hidden flex-col justify-between bg-sidebar p-10 text-sidebar-foreground lg:flex">
        <div className="flex items-center gap-3">
          <div className="flex h-14 w-28 items-center justify-center rounded-xl bg-[#111827] px-3 shadow-sm">
            <img src={esumeiLogoUrl} alt="ESUMEI" className="max-h-10 w-full object-contain" />
          </div>
          <div className="flex flex-col">
            <span className="text-lg font-semibold leading-none">ESUMEI Cloud</span>
          </div>
        </div>
        <div className="space-y-4">
          <h1 className="text-4xl font-bold leading-tight">
            Your metering network. One clear view.
          </h1>
          <p className="text-sidebar-foreground/70">
            ESUMEI Cloud brings energy usage, feeder performance, and connected device
            status into a shared workspace for Anietronic and its customers.
          </p>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.24em] text-sidebar-foreground/45">
            powered by
          </div>
          <div className="mt-2 inline-flex max-w-[210px] rounded-md bg-white px-3 py-1.5 shadow-sm">
            <img
              src={anietronicLogoUrl}
              alt="Anietronic"
              className="h-5 w-auto max-w-[180px] object-contain"
            />
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center bg-background p-6 lg:p-12">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{previewMode ? "Local Access" : "Sign In"}</CardTitle>
            <CardDescription>
              {previewMode
                ? "Select an operator profile to continue in this local environment."
                : "Sign in to your metering workspace."}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {sessionExpired && !previewMode ? (
              <p
                role="status"
                className="rounded-md border border-primary/40 bg-primary/10 px-3 py-2 text-sm"
              >
                Your session expired. Sign in again to continue.
              </p>
            ) : null}
            {previewMode ? (
              <div className="space-y-3">
                <Label>Operator profile</Label>
                <SearchableSelector label="Operator profile" size="form" value={selectedPreviewSubject}
                  placeholder="Choose an operator profile"
                  options={previewUsers.map((user) => ({ value: user.identity_subject, label: `${user.display_name} · ${user.role}` }))}
                  onChange={(value) => {
                    setPreviewSubject(value);
                  }}
                />
                <p className="text-xs text-muted-foreground">
                  Switching profiles applies that membership to requests in the local stack.
                </p>
                <Button className="w-full" onClick={() => navigate("/dashboard")}>
                  Continue
                </Button>
              </div>
            ) : (
              <form className="space-y-4" onSubmit={handleSubmit(onSubmit)} noValidate>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    placeholder="name@company.com"
                    {...register("email")}
                  />
                  {errors.email ? (
                    <p className="text-xs text-destructive">{errors.email.message}</p>
                  ) : null}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    {...register("password")}
                  />
                  {errors.password ? (
                    <p className="text-xs text-destructive">{errors.password.message}</p>
                  ) : null}
                </div>
                {submitError ? (
                  <p
                    role="alert"
                    className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                  >
                    {friendlyLoginError(submitError)}
                  </p>
                ) : null}
                <Button type="submit" className="w-full" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Signing in…
                    </>
                  ) : (
                    "Sign In"
                  )}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function friendlyLoginError(message: string): string {
  const normalized = message.toUpperCase();
  if (
    ["INVALID_LOGIN_CREDENTIALS", "EMAIL_NOT_FOUND", "INVALID_PASSWORD"].some((code) =>
      normalized.includes(code),
    )
  ) {
    return "The email or password is incorrect. Check your details and try again.";
  }
  if (normalized.includes("TOO_MANY_ATTEMPTS_TRY_LATER")) {
    return "Sign-in is temporarily paused after several attempts. Try again later.";
  }
  return message;
}
